import { Button, Flex, HelperText, Input, Typography } from 'components';

import { ReactComponent as LoadingIcon } from 'app/images/loading.svg';

import { createUrl, request } from 'app/utils';
import { useSchool } from 'features/school';
import { useCallback, useEffect, useState } from 'react';
import styled, { useTheme } from 'styled-components';
import { useAccount } from '..';

interface ChangeEmailProps {
  opened: boolean;
  onClose: () => void;
}

const Spinner = styled(LoadingIcon)`
  width: 18px;

  circle {
    stroke: rgba(0, 0, 0, 0.4);
  }
`;

export const ChangeEmail = ({ opened, onClose }: ChangeEmailProps) => {
  const [step, setStep] = useState<'change-email' | 'confirm-current-email' | 'confirm-new-email' | 'success'>('change-email');

  const [loading, setLoading] = useState(false);

  const [newEmail, setNewEmail] = useState('');
  const [confirmationCode, setConfirmationCode] = useState('');

  const [error, setError] = useState<string | null>(null);

  const [openMfa, setOpenMfa] = useState(false);
  const [errorMfa, setErrorMfa] = useState<string | null>(null);
  const [MFACode, setMFACode] = useState('');

  const { apiGatewayUrl } = useSchool();
  const { account } = useAccount();

  const {
    colors: { primary_400 },
  } = useTheme();

  const handleChangeEmail = useCallback(async () => {
    setError(null);

    if (!newEmail) {
      setError('O campo de e-mail não pode estar vazio');
      return;
    }

    if (openMfa && !MFACode) {
      setErrorMfa('O campo de código MFA não pode estar vazio');
      return;
    }

    // validate email
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(newEmail)) {
      setError('Por favor, insira um e-mail válido.');
      return;
    }

    setLoading(true);

    request<{
      needToConfirmPreviousEmail: boolean;
      authenticationResult: number;
      message: string;
    }>({
      url: '/api/v1/User/StartEmailChange',
      method: 'post',
      baseURL: apiGatewayUrl,
      data: {
        newEmail,
        MFACode: MFACode || undefined,
      },
    })
      .then(({ data }) => {
        if (data.authenticationResult === 0) {
          setOpenMfa(false);
          setErrorMfa(null);
          setMFACode('');
          setStep('confirm-current-email');
        } else if (data.authenticationResult === -26) {
          setOpenMfa(true);
          setError(data.message);
        } else {
          setError(data.message || 'Ocorreu um erro ao alterar seu e-mail. Tente novamente mais tarde.');
        }
      })
      .finally(() => {
        setLoading(false);
      });
  }, [newEmail, openMfa, MFACode]);

  const handleConfirmCurrentEmail = useCallback(async () => {
    setError(null);

    if (!confirmationCode) {
      setError('O campo de código não pode estar vazio');
      return;
    }

    setLoading(true);

    request<{
      needToConfirmPreviousEmail: boolean;
      authenticationResult: number;
      message: string;
    }>({
      url: '/api/v1/User/ConfirmCurrentEmail',
      method: 'post',
      baseURL: apiGatewayUrl,
      data: {
        confirmationCode,
      },
    })
      .then(({ data }) => {
        if (data.authenticationResult === 0) {
          setConfirmationCode('');
          setStep('confirm-new-email');
        } else {
          setError(data.message);
        }
      })
      .finally(() => {
        setLoading(false);
      });
  }, [confirmationCode]);

  const handleConfirmaNewEmail = useCallback(async () => {
    setError(null);

    if (!confirmationCode) {
      setError('O campo de código não pode estar vazio');
      return;
    }

    setLoading(true);

    request<{
      needToConfirmPreviousEmail: boolean;
      authenticationResult: number;
      message: string;
    }>({
      url: '/api/v1/User/ConfirmNewEmail',
      method: 'post',
      baseURL: apiGatewayUrl,
      data: {
        confirmationCode,
      },
    })
      .then(({ data }) => {
        if (data.authenticationResult === 0) {
          setStep('success');
        } else {
          setError(data.message);
        }
      })
      .finally(() => {
        setLoading(false);
      });
  }, [confirmationCode]);

  const onCloseSuccess = () => {
    window.location.href = createUrl('account');
  };

  useEffect(() => {
    if (!opened) {
      setNewEmail('');
      setError(null);
      setLoading(false);
      setStep('change-email');
    }
  }, [opened]);

  return opened ? (
    <>
      {/* <Modal onClose={onClose}> */}
      <Flex flexGrow={0} width={{ d: 450 }} p={4} flexDirection="column" style={{ gap: '24px' }}>
        {step === 'change-email' && (
          <div style={{ display: 'flex', flexDirection: 'column', gap: '18px' }}>
            <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
              <Typography type="type4" fontWeight="bold" style={{ textWrap: 'balance' }}>
                Trocar endereço de e-mail
              </Typography>

              <Typography type="type1" color="gray_500" style={{ textWrap: 'balance' }}>
                Por favor, informe o novo e-mail que você deseja associar à sua conta. Você receberá um código de confirmação em seu e-mail atual{' '}
                <span style={{ color: primary_400 }}>({account?.accountLogin})</span>.
              </Typography>
            </div>

            <div>
              <Typography type="type1" fontWeight="bold">
                Novo e-mail
              </Typography>

              <div style={{ position: 'relative' }}>
                <Input
                  data-error={!!error}
                  onChange={(e) => setNewEmail(e.target.value)}
                  value={newEmail}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter') {
                      handleChangeEmail();
                    }
                  }}
                />
              </div>

              {error && <HelperText type="error">{error}</HelperText>}
            </div>

            {openMfa && (
              <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
                <div>
                  <Typography type="type1" fontWeight="bold">
                    Código MFA
                  </Typography>

                  <div style={{ position: 'relative' }}>
                    <Input
                      data-error={!!errorMfa}
                      onChange={(e) => setMFACode(e.target.value)}
                      value={MFACode}
                      onKeyDown={(e) => {
                        if (e.key === 'Enter') {
                          handleChangeEmail();
                        }
                      }}
                      autoFocus
                    />
                  </div>

                  {errorMfa && <HelperText type="error">{errorMfa}</HelperText>}
                </div>
              </div>
            )}

            <Button disabled={loading} type="button" onClick={handleChangeEmail}>
              <div style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
                {loading && <Spinner />}
                Enviar
              </div>
            </Button>
          </div>
        )}

        {step === 'confirm-current-email' && (
          <div style={{ display: 'flex', flexDirection: 'column', gap: '18px' }}>
            <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
              <Typography type="type4" fontWeight="bold" textAlign="center" style={{ textWrap: 'balance' }}>
                Confirme seu e-mail atual
              </Typography>

              <Typography type="type1" color="gray_500" textAlign="center" style={{ textWrap: 'balance' }}>
                Enviamos um e-mail para seu endereço atual contendo um link e um código de confirmação. Insira o código abaixo para validar a
                alteração.
              </Typography>
            </div>

            <div>
              <Typography type="type1" fontWeight="bold">
                Informe o código de confirmação
              </Typography>

              <Input
                data-error={!!error && !errorMfa}
                onChange={(e) => setConfirmationCode(e.target.value)}
                value={confirmationCode}
                onKeyDown={(e) => {
                  if (e.key === 'Enter') {
                    handleConfirmCurrentEmail();
                  }
                }}
                autoFocus
              />

              {error && <HelperText type="error">{error}</HelperText>}
            </div>

            <Button disabled={loading} type="button" onClick={handleConfirmCurrentEmail}>
              <div style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
                {loading && <Spinner />}
                Confirmar e-mail atual
              </div>
            </Button>
          </div>
        )}

        {step === 'confirm-new-email' && (
          <div style={{ display: 'flex', flexDirection: 'column', gap: '18px' }}>
            <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
              <Typography type="type4" fontWeight="bold" textAlign="center" style={{ textWrap: 'balance' }}>
                Confirme seu novo e-mail
              </Typography>

              <Typography type="type1" color="gray_500" textAlign="center" style={{ textWrap: 'balance' }}>
                Tudo certo! Agora enviamos um e-mail para seu novo endereço com outro link e código de confirmação. Insira-o para concluir a
                alteração.
              </Typography>
            </div>

            <div>
              <Typography type="type1" fontWeight="bold">
                Informe o código de confirmação
              </Typography>

              <Input
                data-error={!!error && !errorMfa}
                onChange={(e) => setConfirmationCode(e.target.value)}
                value={confirmationCode}
                onKeyDown={(e) => {
                  if (e.key === 'Enter') {
                    handleConfirmaNewEmail();
                  }
                }}
                autoFocus
              />

              {error && <HelperText type="error">{error}</HelperText>}
            </div>

            <Button disabled={loading} type="button" onClick={handleConfirmaNewEmail}>
              <div style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
                {loading && <Spinner />}
                Confirmar novo e-mail
              </div>
            </Button>
          </div>
        )}

        {step === 'success' && (
          <div style={{ display: 'flex', flexDirection: 'column', gap: '24px' }}>
            <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
              <Typography type="type4" fontWeight="bold" textAlign="center" style={{ textWrap: 'balance' }}>
                Pronto! Seu e-mail foi alterado com sucesso.
              </Typography>
            </div>

            <Button type="button" onClick={onCloseSuccess}>
              Fechar
            </Button>
          </div>
        )}
      </Flex>
      {/* </Modal> */}
    </>
  ) : null;
};
